import React, { useEffect, useState } from "react";
import api from "../../../api/api";
import toast from "react-hot-toast";
import { Edit, Trash2 } from "lucide-react";

const AdminProjectCategories = () => {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: "",
    color: "#0b6b1d",
  });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCategories = async () => {
    try {
      const res = await api.get("/project-categories");
      setCategories(res.data || []);
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load categories");
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const resetForm = () => {
    setForm({
      name: "",
      color: "#0b6b1d",
    });
    setEditing(null);
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    try {
      setLoading(true);

      if (editing) {
        await api.put(`/project-categories/${editing.id}`, form);
        toast.success("Category updated");
      } else {
        await api.post("/project-categories", form);
        toast.success("Category created");
      }

      resetForm();
      loadCategories();
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to save category");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (cat) => {
    setEditing(cat);
    setForm({
      name: cat.name,
      color: cat.color || "#0b6b1d",
    });
  };

  const deleteCategory = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this category?"
    );

    if (!confirmDelete) return;

    try {
      await api.delete(`/project-categories/${id}`);
      toast.success("Category deleted");
      loadCategories();
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to delete category");
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-black text-[#0b6b1d]">
          Project Categories
        </h1>
        <p className="text-slate-500 mt-1">
          Create categories like Completed, Ongoing and Planned.
        </p>
      </div>

      <div className="grid lg:grid-cols-[420px_1fr] gap-6">
        <form
          onSubmit={submit}
          className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm h-fit"
        >
          <h2 className="font-black text-xl text-[#0b6b1d] mb-5">
            {editing ? "Edit Category" : "Add Category"}
          </h2>

          <label className="block mb-5">
            <span className="font-bold text-slate-700">Category Name</span>
            <input
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
              placeholder="Completed"
            />
          </label>

          <label className="block mb-6">
            <span className="font-bold text-slate-700">Badge Color</span>
            <input
              type="color"
              value={form.color}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  color: e.target.value,
                }))
              }
              className="mt-2 w-full h-12 border border-slate-200 rounded-2xl px-2 py-1"
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-2xl bg-[#0b6b1d] hover:bg-[#ff6600] text-white font-black transition disabled:opacity-60"
          >
            {loading ? "Saving..." : editing ? "Update Category" : "Create Category"}
          </button>

          {editing && (
            <button
              type="button"
              onClick={resetForm}
              className="w-full mt-3 py-3 rounded-2xl bg-slate-100 text-slate-700 font-black"
            >
              Cancel Edit
            </button>
          )}
        </form>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
          {categories.length === 0 ? (
            <p className="p-6 text-slate-500">No project category created yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-black text-slate-600">
                      Name
                    </th>
                    <th className="text-left p-4 text-sm font-black text-slate-600">
                      Badge
                    </th>
                    <th className="text-right p-4 text-sm font-black text-slate-600">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {categories.map((cat) => (
                    <tr key={cat.id} className="border-b last:border-b-0">
                      <td className="p-4 font-black text-slate-900">
                        {cat.name}
                      </td>

                      <td className="p-4">
                        <span
                          className="px-4 py-2 rounded-full text-xs font-black text-white"
                          style={{ backgroundColor: cat.color || "#0b6b1d" }}
                        >
                          {cat.name}
                        </span>
                      </td>

                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => startEdit(cat)}
                            className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-[#0b6b1d] hover:text-white flex items-center justify-center"
                          >
                            <Edit size={17} />
                          </button>

                          <button
                            onClick={() => deleteCategory(cat.id)}
                            className="w-10 h-10 rounded-xl bg-red-50 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center"
                          >
                            <Trash2 size={17} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProjectCategories;